import React from 'react';

import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { FiberManualRecord } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

import { toLocaleString } from '../../utils/format-number';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    border: '1px solid #00000014',
    borderRadius: '12px',
    padding: theme.spacing(2),
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(1),
    height: '10px',
    width: '10px',
  },
  itemText: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#0000000D',
    margin: theme.spacing(1, 0),
  },
}));

export interface SummaryCardItem {
  label: string;
  value: number;
  color: string;
}

interface SummaryCardProps {
  isFetching?: boolean;
  cardHeading?: string;
  totalModels?: number;
  totalFreelancers?: number;
  selectedLabel?: string;
  // Rows default to the Models/Freelancer split; cards with a different breakdown
  // (e.g. Re-applications -> In review/Accepted/Rejected) pass their own. The heading
  // shows the sum of the rows unless `total` says otherwise.
  items?: SummaryCardItem[];
  total?: number;
}

export const SummaryCard: React.FC<SummaryCardProps> = ({
  isFetching = false,
  cardHeading,
  totalModels = 0,
  totalFreelancers = 0,
  selectedLabel = '',
  items,
  total,
}) => {
  const classes = useStyles();

  const rows: SummaryCardItem[] = items ?? [
    { label: 'Models', value: totalModels, color: '#FCC584' },
    { label: 'Freelancer', value: totalFreelancers, color: '#BEDEC5' },
  ];

  return (
    <Box className={classes.container}>
      <Box display={'flex'} alignItems={'flex-end'} mb={2} justifyContent={'space-between'}>
        <Typography style={{ fontSize: '15px', margin: 0 }}>
          {cardHeading}&nbsp;
          <Typography component={'span'} style={{ fontWeight: 500 }}>
            {toLocaleString(total ?? rows.reduce((acc, row) => acc + row.value, 0))}
          </Typography>
        </Typography>
        <Typography style={{ fontSize: '13px', margin: 0, color: 'rgba(0, 0, 0, 0.50)' }}>
          {selectedLabel}
        </Typography>
      </Box>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <Box className={classes.itemRow}>
            <Box className={classes.itemText}>
              <FiberManualRecord className={classes.icon} style={{ color: row.color }} />
              <Typography
                variant="body2"
                style={{
                  fontSize: '13px',
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  lineHeight: '19.5px',
                }}
              >
                {row.label}
              </Typography>
            </Box>
            {isFetching && (
              <Skeleton
                animation="wave"
                variant="rect"
                width={50}
                height={10}
                style={{ marginTop: '5px', borderRadius: '5px' }}
              />
            )}
            {!isFetching && (
              <Typography variant="body2">
                <span
                  style={{
                    fontSize: '15px',
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    color: '#111111',
                  }}
                >
                  {toLocaleString(row.value)}
                </span>
              </Typography>
            )}
          </Box>
          <div className={classes.divider}></div>
        </React.Fragment>
      ))}
    </Box>
  );
};
